import { useEffect, useState } from 'react'
import { onCoachCall } from '../api/client'
import type { DebugEntry } from '../types'

function bytes(n: number | undefined): string {
  if (n == null) return ''
  return n < 1024 ? n + ' B' : (n / 1024).toFixed(1) + ' kB'
}

function tokens(e: DebugEntry): string {
  const u = e.usage ?? {}
  const i = u.input_tokens ?? u.promptTokenCount
  const o = u.output_tokens ?? u.candidatesTokenCount
  if (i == null && o == null) return ''
  return ` · in ${i ?? '?'} tok · out ${o ?? '?'} tok`
}

function Fold({ label, body, size }: { label: string; body?: string; size?: number }) {
  if (!body) return null
  return (
    <details>
      <summary>
        {label} <span className="sz">({bytes(size)})</span>
      </summary>
      <pre>{body}</pre>
    </details>
  )
}

function Entry({ e }: { e: DebugEntry }) {
  const sent = (e.system_bytes ?? 0) + (e.user_bytes ?? 0)
  return (
    <>
      {e.error ? (
        <div className="row err">
          ✗ {e.provider}
          {e.model ? ' ' + e.model : ''} · {e.ms} ms · {e.error}
        </div>
      ) : (
        <div className="row ok">
          ✓ {e.provider}
          {e.model ? ' ' + e.model : ''}
          {e.status ? ' · ' + e.status : ''} · {e.ms} ms{tokens(e)}
        </div>
      )}
      {sent > 0 && (
        <div className="row dim">
          envoyé : {bytes(sent)} (system {bytes(e.system_bytes)} + user {bytes(e.user_bytes)})
        </div>
      )}
      {e.api_error != null && <div className="row err">{JSON.stringify(e.api_error)}</div>}
      <Fold label="system" body={e.system} size={e.system_bytes} />
      <Fold label="user (capture + état)" body={e.user} size={e.user_bytes} />
      <Fold label="réponse brute" body={e.raw} size={e.raw_bytes} />
    </>
  )
}

/**
 * Visible seulement si l'API renvoie une trace (COACH_DEBUG=1) : c'est la
 * fenêtre sur ce que le coach a réellement reçu et répondu.
 */
export default function DebugConsole() {
  const [entries, setEntries] = useState<DebugEntry[] | null>(null)
  const [status, setStatus] = useState('')
  const [pending, setPending] = useState(false)
  const [minimized, setMinimized] = useState(false)

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined
    const off = onCoachCall((c) => {
      if (c.phase === 'start') {
        const t0 = performance.now()
        setPending(true)
        clearInterval(timer)
        timer = setInterval(() => {
          setStatus(`⋯ appel coach ${((performance.now() - t0) / 1000).toFixed(1)}s`)
        }, 100)
        return
      }
      clearInterval(timer)
      setPending(false)
      setEntries(c.entries ?? null)
      if (c.entries) {
        setStatus(`${c.entries.length} appel${c.entries.length > 1 ? 's' : ''} · ${c.ms} ms aller-retour`)
      }
    })
    return () => {
      clearInterval(timer)
      off()
    }
  }, [])

  useEffect(() => {
    document.body.classList.toggle('has-dbg', entries != null)
  }, [entries])

  if (!entries) return null

  return (
    <div className={'dbg' + (minimized ? ' min' : '')}>
      <div className="bar" onClick={() => setMinimized(!minimized)}>
        <span className="ttl">coach debug</span>
        <span className="st">{pending ? <span className="pend">{status}</span> : status}</span>
        <span className="x">{minimized ? '▴' : '▾'}</span>
      </div>
      <div className="log">
        {entries.map((e, i) => (
          <Entry key={i} e={e} />
        ))}
      </div>
    </div>
  )
}
